// Trang "Gửi thông báo" (docs/KE_HOACH_GUI_THONG_BAO.md §3, §5) — kiểu dữ liệu + hàm thuần dùng chung cho khu
// Đại lý / CTV (apps/api /network/announcements) và khu Admin NPP (apps/admin-api /announcements).

import type { AnnounceKind } from './AnnouncementView'

export type AnnounceUnitType = 'agency' | 'ctv' | 'shop'
export type AnnounceScope = 'all' | 'branches' | 'units'

export const UNIT_TYPE_LABEL: Record<AnnounceUnitType, string> = { agency: 'Đại lý', ctv: 'CTV', shop: 'Shop' }
export const UNIT_TYPE_TONE: Record<AnnounceUnitType, string> = { agency: 'tone-orange', ctv: 'tone-violet', shop: 'tone-green' }
export const UNIT_TYPE_ORDER: AnnounceUnitType[] = ['agency', 'ctv', 'shop']

export const TITLE_MAX = 80
export const BODY_MAX = 1000

export interface Audience {
  scope: AnnounceScope
  types: AnnounceUnitType[]
  ids?: string[]
  includeStaff?: boolean
}

export interface SendBody {
  kind: AnnounceKind
  title: string
  body: string
  linkUrl?: string | null
  expiresAt?: string | null
  audience: Audience
}

export interface AnnItem {
  id: string
  senderKind: 'platform' | 'npp' | 'unit'
  senderName: string | null
  kind: string
  title: string
  body: string
  linkUrl: string | null
  expiresAt: string | null
  includeStaff: boolean
  unitCount: number
  recipientCount: number
  readCount: number
  createdAt: string
  recalledAt: string | null
  canRecall: boolean
}

export interface AnnList {
  items: AnnItem[]
  total: number
  page: number
  limit: number
}

export interface AnnPreview {
  units: number
  recipients: number
}

/** Cách gọi API của một khu: `get/post` (apps/web/src/lib/api.ts) hoặc `adminGet/adminPost` (lib/adminApi.ts). */
export interface AnnounceApi {
  /** Phân biệt cache react-query giữa các khu / đơn vị */
  area: string
  /** '/network/announcements' | '/announcements' */
  base: string
  get: <T>(path: string) => Promise<T>
  post: <T>(path: string, body?: unknown) => Promise<T>
}

/** Một đơn vị để chọn (nhánh / từng đơn vị). parId null = gốc của danh sách. */
export interface AnnounceUnit {
  id: string
  name: string
  tenantType: AnnounceUnitType
  parId: string | null
  owner?: { name: string | null; phone: string | null } | null
}

export const fmtNum = (n: number | null | undefined) => (Number.isFinite(n) ? (n as number) : 0).toLocaleString('vi-VN')

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const tz = { timeZone: 'Asia/Ho_Chi_Minh' } as const
  return `${d.toLocaleDateString('vi-VN', { ...tz, day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('vi-VN', { ...tz, hour: '2-digit', minute: '2-digit' })}`
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })
}

/** Hôm nay theo giờ Việt Nam, dạng yyyy-mm-dd (giá trị của <input type="date">). */
export function todayVN(now = new Date()): string {
  return new Date(now.getTime() + 7 * 3_600_000).toISOString().slice(0, 10)
}

/** Ngày hết hạn chọn trên ô ngày → cuối ngày đó giờ Việt Nam (ISO). Rỗng → null. */
export function expiresIso(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
  const d = new Date(`${date}T23:59:59+07:00`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** Link kèm thông báo: trang trong hệ thống ("/rates") hoặc https://. Trả về lỗi (chuỗi) hoặc null. */
export function linkError(raw: string): string | null {
  const v = raw.trim()
  if (!v) return null
  if (v.length > 500) return 'Link quá dài'
  if (v.startsWith('/') && !v.startsWith('//')) return null
  try {
    if (new URL(v).protocol === 'https:') return null
  } catch {}
  return 'Link phải bắt đầu bằng / hoặc https://'
}

/** Bộ lọc người nhận gửi lên máy chủ — null khi chưa đủ (chưa chọn loại / nhánh / đơn vị). */
export function buildAudience(s: {
  scope: AnnounceScope
  types: AnnounceUnitType[]
  branchIds: string[]
  unitIds: string[]
  includeStaff: boolean
}): Audience | null {
  if (s.scope === 'units') return s.unitIds.length ? { scope: 'units', types: [], ids: [...s.unitIds].sort(), includeStaff: s.includeStaff } : null
  if (!s.types.length) return null
  const types = UNIT_TYPE_ORDER.filter((t) => s.types.includes(t))
  if (s.scope === 'branches') return s.branchIds.length ? { scope: 'branches', types, ids: [...s.branchIds].sort(), includeStaff: s.includeStaff } : null
  return { scope: 'all', types, includeStaff: s.includeStaff }
}
